import React from 'react';
import { CheckCircle2, Circle, ListChecks, SkipForward } from 'lucide-react';
import type { AgendaResolvedState, AgendaSegment } from '../utils/agendaTimeline.ts';

interface AgendaPanelProps {
  segments: AgendaSegment[];
  resolved: AgendaResolvedState;
  isHost: boolean;
  canAdvance: boolean;
  onAdvance: () => void;
}

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const AgendaPanel: React.FC<AgendaPanelProps> = ({
  segments,
  resolved,
  isHost,
  canAdvance,
  onAdvance,
}) => {
  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="hidden md:flex items-center justify-between gap-2 p-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks className="w-4 h-4 text-purple-400 flex-shrink-0" />
          <h3 className="text-white font-semibold truncate">Gündem</h3>
        </div>
        {isHost && (
          <button
            type="button"
            onClick={onAdvance}
            disabled={!canAdvance}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition"
            title="Sonraki maddeye geç"
          >
            <SkipForward className="w-3.5 h-3.5" />
            Sonraki
          </button>
        )}
      </div>

      {isHost && (
        <div className="md:hidden px-4 pt-3 flex-shrink-0">
          <button
            type="button"
            onClick={onAdvance}
            disabled={!canAdvance}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition"
          >
            <SkipForward className="w-4 h-4" />
            Sonraki maddeye geç
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
        {segments.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">
            Bu toplantıya gündem eklenmemiş.
          </p>
        ) : (
          segments.map((seg) => {
            const isActive = !resolved.isComplete && resolved.index === seg.index;
            const isDone =
              resolved.isComplete ||
              (resolved.segment != null && seg.index < resolved.index);
            return (
              <div
                key={seg.id}
                className={`rounded-xl border p-3 transition ${
                  isActive
                    ? 'bg-purple-600/20 border-purple-500/40 ring-1 ring-purple-500/30'
                    : isDone
                      ? 'bg-white/[0.03] border-white/5 opacity-70'
                      : 'bg-white/5 border-white/10'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 flex-shrink-0">
                    {isDone && !isActive ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Circle
                        className={`w-4 h-4 ${
                          isActive ? 'text-purple-300' : 'text-gray-500'
                        }`}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-sm font-medium truncate ${
                          isActive ? 'text-white' : 'text-gray-200'
                        }`}
                      >
                        {seg.index + 1}. {seg.title}
                      </p>
                      <span className="text-[11px] text-gray-400 tabular-nums flex-shrink-0">
                        {seg.durationSec >= 60
                          ? `${Math.round(seg.durationSec / 60)} dk`
                          : `${seg.durationSec} sn`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          seg.type === 'karar'
                            ? 'bg-amber-500/15 text-amber-300'
                            : 'bg-blue-500/15 text-blue-300'
                        }`}
                      >
                        {seg.type === 'karar' ? 'Karar' : 'Bilgi'}
                      </span>
                      {isActive && (
                        <span className="text-xs text-purple-300 font-mono tabular-nums">
                          Kalan {formatRemaining(resolved.remainingSec)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AgendaPanel;
